import {LocalStorageKeys} from "@app/shared/enums";
import {NzSafeAny} from "ng-zorro-antd/core/types";
import {CookieConsent} from "@app/shared/interfaces";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AppStateService} from "@app/shared/services/app-state.service";
import {DestroyRef, inject, Injectable, TemplateRef} from '@angular/core';
import {getItemStore, removeItemStore, setItemStore} from "@app/shared/helpers/storage";
import {NzNotificationComponent, NzNotificationService} from "ng-zorro-antd/notification";

@Injectable({
  providedIn: 'root'
})
export class CookieService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStateService = inject(AppStateService);
  private readonly nzNotificationService = inject(NzNotificationService);

  public init() {
    const consent = this.getCookieConsent();

    if (consent) {
      const ONE_YEAR = 365 * 24 * 60 * 60 * 1000;

      if (Date.now() - consent.timestamp > ONE_YEAR) {
        removeItemStore([LocalStorageKeys.cookieConsent]);
        this.appStateService.cookieConsent = null;
      }

      if (consent.accepted) {
        this.appStateService.cookieConsent = this.getCookieConsent();
        this.loadOptionalAnalyticsAndSupport();
      }
    } else {
      this.appStateService.cookieConsent = null;
    }
  }

  public acceptCookies() {
    const data = {accepted: true, timestamp: Date.now()};
    setItemStore<CookieConsent>(LocalStorageKeys.cookieConsent, data);

    this.hideCookieBanner();
    this.appStateService.cookieConsent = data;
    this.loadOptionalAnalyticsAndSupport();
  }

  public loadGoogleSignIn(): Promise<void> {
    return this.loadScript('google-sign-in-script', 'https://accounts.google.com/gsi/client');
  }

  private loadOptionalAnalyticsAndSupport() {
    void this.loadScript('google-analytics-script', 'https://www.googletagmanager.com/gtag/js?id=G-3ZMHWQBCD5')
      .catch(() => undefined);
    void this.loadScript('ahrefs-analytics-script', 'https://analytics.ahrefs.com/analytics.js', {
      'data-key': '7YaT76/pELnq8Ua5rv80XQ'
    }).catch(() => undefined);

    const browserWindow = window as typeof window & { $crisp?: unknown[]; CRISP_WEBSITE_ID?: string };
    browserWindow.$crisp ??= [];
    browserWindow.$crisp.push(['do', 'chat:hide'], ['safe', true]);
    browserWindow.CRISP_WEBSITE_ID = '1399a80b-2534-4554-8225-fa69371e9201';
    void this.loadScript('crisp-chat-script', 'https://client.crisp.chat/l.js').catch(() => undefined);
  }

  private loadScript(id: string, src: string, attributes: Record<string, string> = {}): Promise<void> {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing?.dataset['loaded'] === 'true') return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = existing || document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      Object.entries(attributes).forEach(([name, value]) => script.setAttribute(name, value));
      script.addEventListener('load', () => {
        script.dataset['loaded'] = 'true';
        resolve();
      }, {once: true});
      script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {once: true});
      if (!existing) document.head.appendChild(script);
    });
  }

  private getCookieConsent() {
    return getItemStore<CookieConsent | null>(LocalStorageKeys.cookieConsent) || null;
  }

  public showCookieBanner(cookieTmRef: TemplateRef<{ $implicit: NzNotificationComponent; data: NzSafeAny }>) {
    const condition = !location.href.includes('template-print') &&
      !location.href.includes('forResumeExamples') &&
      !this.appStateService.cookieConsent?.accepted &&
      !this.appStateService.isAuthenticated

    if (condition) {
      setTimeout(() => {
        this.nzNotificationService.template(cookieTmRef, {
          nzDuration: 30 * 1000,
          nzPlacement: 'bottomLeft',
          nzClass: 'sf-cookie-notification',
          nzStyle: {borderRadius: '8px'},
          nzKey: 'CookieConsent'
        })
          .onClose
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.acceptCookies());
      }, 1200);
    }
  }

  private hideCookieBanner() {
    this.nzNotificationService.remove('CookieConsent')
  }

}
